import {formatISO} from "date-fns";
import capitalize from "lodash/capitalize";

import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import {SummaryBar} from "@/components/summaryBar/SummaryBar.tsx";
import {SummaryBlock} from "@/components/summaryBar/SummaryBar.types.ts";
import {dateOnly, shortCurrency} from "@/lib/format.tsx";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {BillingPeriod} from "@/lib/types/billing.ts";

interface SubscriptionSummaryStats {
  billAmount: number;
  billPeriodMonths: number;
  nextBillingDate?: Date;
}

export const SubscriptionSummaryCustomer = () => {
  const {user} = useCurrentUserContext();
  const checkoutContext = useCheckoutContext();
  const isAnnual = user?.account.billing_period === BillingPeriod.YEAR;
  const billAmount = checkoutContext.billingAmount || 0;
  const nextBillingDate = checkoutContext.nextBillingDate;
  const billPeriodMonths = isAnnual ? 12 : 1;

  const stats: SubscriptionSummaryStats = {
    billAmount,
    billPeriodMonths,
    nextBillingDate,
  };

  const summaryBlocks: SummaryBlock<SubscriptionSummaryStats>[] = [
    {
      title: "Current Subscription Total",
      renderStat: () => <>{`${shortCurrency(billAmount, 2)}/${billPeriodMonths > 1 ? "yr" : "mo"}`}</>,
    },
    {
      title: "Billing Period",
      renderStat: () => <>{billPeriodMonths > 1 ? "Annual" : "Monthly"}</>,
    },
  ];

  if (user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0) {
    summaryBlocks.unshift({
      title: "Current Plan",
      renderStat: () => <>{capitalize(user?.account.plan)}</>,
    });
  }

  if (nextBillingDate) {
    summaryBlocks.push({
      title: "Next Charge",
      renderStat: () => <>{dateOnly(formatISO(nextBillingDate), user!.current_date_format, user!.account!.timezone)}</>,
    });
  }

  return (
    <SummaryBar
      stats={stats}
      summaryBlocks={summaryBlocks}
      columns={{
        sm: 1,
        md: 2,
        lg: 2,
        xl: summaryBlocks.length,
      }}
    />
  );
};
